import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Badge } from "../components/kr";
import { MoneyInput } from "../components/MoneyInput";
import { toast } from "sonner";
import {
  Bank, Calculator, CaretRight, ArrowDown, ArrowUp, CheckCircle, XCircle, TrendUp, ArrowRight
} from "@phosphor-icons/react";
import { inr } from "../lib/format";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

// ---------------------------------------------------------------- helpers
const money = (v) => inr(v || 0);

// ---------------------------------------------------------------- page
export default function Loans() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/loans");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const markPayment = async (loan, n, status) => {
    try {
      await api.post(`/loans/${loan.id}/pay`, { n, status });
      toast.success(status === "paid" ? "Marked paid" : "Marked " + status);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const loans = data?.loans || [];
  const totalOutstanding = loans.reduce((s, l) => s + l.outstanding_principal, 0);
  const totalMonthly = loans.reduce((s, l) => s + l.monthly_emi, 0);
  const totalInterestPending = loans.reduce((s, l) => s + l.total_interest_pending, 0);
  const totalInterestPaid = loans.reduce((s, l) => s + l.total_interest_paid, 0);

  return (
    <div className="p-6 lg:p-10 bg-zinc-50/50 min-h-screen space-y-8" data-testid="loans-page">
      <PageHeader
        label="ACCOUNTING · LOANS"
        title="Business Loans"
        subtitle={`${loans.length} active loan${loans.length === 1 ? "" : "s"}`}
      />

      {/* Aggregate stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="loans-stats">
        <Stat label="Total outstanding" value={money(totalOutstanding)} accent="text-rose-600" bgAccent="bg-rose-50/50" icon={Bank} />
        <Stat label="Monthly drain" value={money(totalMonthly)} accent="text-amber-600" bgAccent="bg-amber-50/50" icon={ArrowDown} />
        <Stat label="Interest paid" value={money(totalInterestPaid)} accent="text-emerald-600" bgAccent="bg-emerald-50/50" icon={ArrowUp} />
        <Stat label="Interest yet-to-pay" value={money(totalInterestPending)} accent="text-indigo-600" bgAccent="bg-indigo-50/50" icon={Calculator} />
      </div>

      {loans.length === 0 ? (
        <Card className="border border-dashed border-zinc-300 shadow-sm bg-white rounded-xl" data-testid="loans-empty">
          <div className="flex flex-col items-center justify-center text-center gap-4 py-12 px-6">
            <div className="p-3 rounded-full bg-zinc-100 text-zinc-500">
              <Bank size={28} weight="duotone" />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold text-zinc-800">No loans found</div>
              <div className="text-xs text-zinc-500 max-w-md">
                Log a business loan or capital injection in the <span className="font-mono font-semibold text-zinc-700">Capital &amp; Loans</span> ledger to see its amortization schedule, EMI drain and payoff simulator here.
              </div>
            </div>
            <Link
              to="/company-expenses?tab=capital"
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-semibold px-4 py-2 rounded-lg tracking-wide transition-all active:scale-95 shadow-sm"
              data-testid="loans-empty-configure-link"
            >
              Configure a loan
              <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {loans.map(loan => {
            const isOpen = !!expanded[loan.id];
            const paidPct = loan.total_emis > 0
              ? Math.round((loan.total_paid_emis / loan.total_emis) * 100) : 0;
            return (
              <div key={loan.id} className="bg-white border border-zinc-200/80 rounded-xl shadow-sm overflow-hidden transition-all duration-200 hover:border-zinc-300" data-testid={`loan-${loan.id}`}>
                <button
                  onClick={() => setExpanded(x => ({ ...x, [loan.id]: !isOpen }))}
                  className="w-full flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-5 hover:bg-zinc-50/60 text-left transition-colors"
                  data-testid={`loan-toggle-${loan.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-1 rounded bg-zinc-100 text-zinc-600">
                      <CaretRight size={16} weight="bold"
                        className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-900 flex items-center gap-2 flex-wrap">
                        <span className="text-base">{loan.source}</span>
                        {loan.moratorium_months > 0 && (
                          <Badge tone="yellow" data-testid={`loan-mor-badge-${loan.id}`}>
                            {loan.moratorium_months}mo moratorium
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs font-mono text-zinc-500 mt-0.5">
                        {money(loan.amount)} @ {loan.interest_rate_percent || 0}% p.a. · started {loan.date}
                        {loan.loan_end_date ? ` · ends ${loan.loan_end_date}` : ""}
                      </div>
                    </div>
                  </div>
                  
                  <div className="sm:text-right shrink-0 flex sm:flex-col justify-between items-center sm:items-end border-t sm:border-t-0 pt-3 sm:pt-0 border-zinc-100">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Outstanding Principal</div>
                    <div className="font-mono font-bold text-xl text-rose-600">{money(loan.outstanding_principal)}</div>
                  </div>
                </button>

                {/* Progress bar area inside card wrapper */}
                <div className="px-6 pb-5">
                  <div className="flex items-center justify-between text-[11px] font-mono text-zinc-500 mb-1.5">
                    <span className="font-medium text-zinc-700">{loan.total_paid_emis} / {loan.total_emis} EMIs paid ({paidPct}%)</span>
                    <span>Payoff: <strong className="text-zinc-800">{loan.payoff_date || "—"}</strong> · {loan.months_remaining} mo left</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-full border border-zinc-200/60 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                         style={{ width: `${paidPct}%` }} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-zinc-200/80 p-6 bg-zinc-50/30 space-y-6">
                    {/* EMI drain chart */}
                    <ChartBlock schedule={loan.schedule} />

                    {/* Simulator + summary */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm">
                        <div className="text-xs font-semibold text-zinc-400 tracking-wider uppercase mb-3">Interest breakdown</div>
                        <div className="divide-y divide-zinc-100">
                          <RowKV label="Total Lifetime Interest" value={money(loan.total_interest_paid + loan.total_interest_pending)} />
                          <RowKV label="Paid to date" value={money(loan.total_interest_paid)} accent="text-zinc-700" />
                          <RowKV label="Yet to pay" value={money(loan.total_interest_pending)} accent="text-rose-600" />
                          <RowKV label="Regular monthly EMI" value={money(loan.monthly_emi)} accent="text-amber-600" bold />
                          {loan.moratorium_months > 0 && (
                            <div className="pt-2 mt-2">
                              <RowKV label={`Moratorium (first ${loan.moratorium_months}mo)`} value={money(loan.moratorium_emi)} accent="text-yellow-700" bold />
                              <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed italic">
                                {loan.moratorium_amount > 0
                                  ? "Fixed grace-period payment · principal untouched."
                                  : "Interest-only · principal untouched during grace period."}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <SimulatorCard loanId={loan.id} baseInterest={loan.total_interest_paid + loan.total_interest_pending} />
                    </div>

                    {/* Amortization schedule */}
                    <ScheduleTable loan={loan} onMark={markPayment} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- sub-components
function Stat({ label, value, accent, bgAccent, icon: Icon }) {
  return (
    <div className="border border-zinc-200 bg-white p-5 rounded-xl shadow-sm flex items-center justify-between gap-4">
      <div className="space-y-1">
        <div className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">{label}</div>
        <div className={`font-mono font-bold text-2xl tracking-tight ${accent || "text-zinc-900"}`}>{value}</div>
      </div>
      {Icon && (
        <div className={`p-3 rounded-xl ${bgAccent || "bg-zinc-100"} ${accent || "text-zinc-500"} shrink-0`}>
          <Icon size={20} weight="duotone" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- RowKV
function RowKV({ label, value, accent, bold }) {
  return (
    <div className="flex justify-between items-center py-2.5 text-sm">
      <span className="text-zinc-500 font-medium">{label}</span>
      <span className={`font-mono ${bold ? "font-bold text-base" : "font-semibold"} ${accent || "text-zinc-800"}`}>{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------- ChartBlock
function ChartBlock({ schedule }) {
  const chartData = useMemo(() => {
    if (!schedule?.length) return [];
    const step = Math.max(1, Math.floor(schedule.length / 24));
    return schedule.filter((_, i) => i % step === 0 || i === schedule.length - 1)
      .map(r => ({ n: r.n, principal: r.principal, interest: r.interest, balance: r.balance }));
  }, [schedule]);
  if (!chartData.length) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm">
      <div className="text-xs font-semibold text-zinc-400 tracking-wider uppercase mb-4">Amortization Trajectory</div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={chartData} margin={{ top: 4, right: 10, left: -10, bottom: 4 }}>
          <CartesianGrid stroke="#f4f4f5" strokeDasharray="3 3" />
          <XAxis dataKey="n" tick={{ fontSize: 10, fontFamily: "monospace", fill: "#71717a" }} />
          <YAxis tick={{ fontSize: 10, fontFamily: "monospace", fill: "#71717a" }}
                 tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
          <Tooltip 
            contentStyle={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace' }}
            formatter={(v) => money(v)} 
            labelFormatter={(v) => `Month ${v}`} 
          />
          <Line type="monotone" dataKey="principal" stroke="#6366f1" strokeWidth={2.5} dot={false} name="Principal" />
          <Line type="monotone" dataKey="interest" stroke="#f43f5e" strokeWidth={2.5} dot={false} name="Interest" />
          <Line type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={false} name="Balance" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------- SimulatorCard
function SimulatorCard({ loanId, baseInterest }) {
  const [extra, setExtra] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const n = parseFloat(extra) || 0;
    if (n <= 0) { toast.error("Enter an amount > 0"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/loans/${loanId}/simulate`, { params: { extra_monthly: n } });
      setResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm flex flex-col justify-between">
      <div>
        <div className="text-xs font-semibold text-zinc-400 tracking-wider uppercase mb-3">Early-repayment simulator</div>
        <div className="flex gap-2 items-end mb-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mb-1">Extra monthly investment</div>
            <MoneyInput value={extra} onChange={e => setExtra(e.target.value)} min={0}
                        placeholder="₹0.00"
                        className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                        data-testid={`sim-extra-${loanId}`} />
          </div>
          <Btn onClick={run} disabled={busy} data-testid={`sim-run-${loanId}`} className="h-[38px] px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center">
            {busy ? "…" : "Simulate"}
          </Btn>
        </div>
      </div>
      {result && (
        <div className="text-xs space-y-1.5 border-t border-zinc-100 pt-3 mt-auto bg-emerald-50/40 p-3 rounded-lg border border-emerald-100" data-testid={`sim-result-${loanId}`}>
          <RowKV label="Months saved" value={result.months_saved} accent="text-emerald-700" bold />
          <RowKV label="Interest saved" value={money(result.interest_saved)} accent="text-emerald-700" bold />
          <RowKV label="New payoff date" value={result.with_extra?.payoff || "—"} accent="text-zinc-700" />
          <RowKV label="New EMI" value={money(result.with_extra?.emi || 0)} accent="text-zinc-700" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ScheduleTable
function ScheduleTable({ loan, onMark }) {
  const [showAll, setShowAll] = useState(false);
  const rows = showAll ? loan.schedule : loan.schedule.slice(0, 12);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
      <div className="p-4 bg-zinc-50/50 border-b border-zinc-200 flex items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Amortization schedule</div>
          <div className="text-[11px] font-mono text-zinc-400 mt-0.5">
            {loan.total_emis} total periods · showing {rows.length}
          </div>
        </div>
        <button onClick={() => setShowAll(!showAll)}
                className="text-xs font-semibold tracking-wider text-indigo-600 hover:text-indigo-800 transition-colors uppercase">
          {showAll ? "Show partial" : `Show all (${loan.total_emis})`}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-zinc-400 font-mono uppercase text-[10px] tracking-wider">
              <th className="p-3 pl-4">#</th>
              <th className="p-3">Due</th>
              <th className="p-3 text-right">Principal</th>
              <th className="p-3 text-right">Interest</th>
              <th className="p-3 text-right">EMI</th>
              <th className="p-3 text-right">Balance</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 font-mono">
            {rows.map(r => (
              <tr key={r.n} data-testid={`sched-${loan.id}-${r.n}`} className={`${r.phase === "moratorium" ? "bg-amber-50/40 hover:bg-amber-50/70" : "hover:bg-zinc-50/50"} transition-colors`}>
                <td className="p-3 pl-4 font-semibold text-zinc-600">
                  {r.n}
                  {r.phase === "moratorium" && (
                    <span className="ml-1.5 px-1 py-0.5 text-[9px] font-bold rounded bg-amber-100 text-amber-800 uppercase tracking-wide">mor</span>
                  )}
                </td>
                <td className="p-3 text-zinc-600">{r.due}</td>
                <td className="p-3 text-right text-zinc-700">{money(r.principal)}</td>
                <td className="p-3 text-right text-rose-600">{money(r.interest)}</td>
                <td className="p-3 text-right font-bold text-zinc-900">{money(r.emi)}</td>
                <td className="p-3 text-right text-zinc-400">{money(r.balance)}</td>
                <td className="p-3 text-center">
                  <span className="inline-block scale-95">
                    <Badge tone={r.status === "paid" ? "green" : r.status === "skipped" ? "red" : "zinc"}>
                      {r.status}
                      {r.source === "manual" && " ★"}
                    </Badge>
                  </span>
                </td>
                <td className="p-3 pr-4 text-right">
                  <div className="flex justify-end gap-2">
                    {r.status !== "paid" && (
                      <button onClick={() => onMark(loan, r.n, "paid")}
                              className="text-emerald-600 hover:text-emerald-800 transition-colors p-1 hover:bg-emerald-50 rounded"
                              data-testid={`sched-paid-${loan.id}-${r.n}`}
                              title="Mark paid">
                        <CheckCircle size={16} weight="fill" />
                      </button>
                    )}
                    {r.status === "paid" && r.source === "manual" && (
                      <button onClick={() => onMark(loan, r.n, "upcoming")}
                              className="text-zinc-400 hover:text-rose-600 transition-colors p-1 hover:bg-zinc-100 rounded"
                              title="Revert to auto"
                              data-testid={`sched-revert-${loan.id}-${r.n}`}>
                        <XCircle size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}